import { useState, useRef } from "react";
import apiClient from "@/utils/apiClient";
import { Upload, X, FileText, Image, File as FileIcon, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const FILE_ICONS = {
  "application/pdf": FileText,
  "text/plain": FileText,
  "text/csv": FileText,
  "application/msword": FileText,
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": FileText,
  "image/png": Image,
  "image/jpeg": Image,
  "image/webp": Image,
};

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

export default function FileUpload({ files = [], onChange, maxFiles = 5 }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const handleUpload = async (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (!selectedFiles.length) return;
    if (files.length + selectedFiles.length > maxFiles) {
      setError(`Maximum ${maxFiles} files allowed`);
      return;
    }

    setUploading(true);
    setError("");

    const newFiles = [];
    for (const file of selectedFiles) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        const res = await apiClient.post("/admin/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newFiles.push(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || `Failed to upload ${file.name}`);
      }
    }

    if (newFiles.length) {
      onChange([...files, ...newFiles]);
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const removeFile = (fileId) => {
    onChange(files.filter((f) => f.file_id !== fileId));
  };

  return (
    <div data-testid="file-upload-component">
      {/* Upload area */}
      <div
        className="border-2 border-dashed border-white/10 rounded-xl p-4 text-center hover:border-amber-500/30 transition-colors cursor-pointer"
        onClick={() => inputRef.current?.click()}
        data-testid="file-upload-area"
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.txt,.csv,.png,.jpg,.jpeg,.webp"
          onChange={handleUpload}
          className="hidden"
          data-testid="file-upload-input"
        />
        {uploading ? (
          <div className="flex items-center justify-center gap-2 py-2">
            <Loader2 className="w-5 h-5 text-amber-400 animate-spin" />
            <span className="text-white/50 text-sm">Uploading...</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1.5 py-1">
            <Upload className="w-5 h-5 text-white/30" />
            <p className="text-white/40 text-xs">
              Click to upload PDF, Word, TXT, CSV, or images (max {maxFiles} files, 20MB each)
            </p>
          </div>
        )}
      </div>

      {error && (
        <p className="text-red-400 text-xs mt-2" data-testid="file-upload-error">{error}</p>
      )}

      {/* Attached files list */}
      {files.length > 0 && (
        <div className="mt-3 space-y-2" data-testid="file-upload-list">
          {files.map((f) => {
            const Icon = FILE_ICONS[f.content_type] || FileIcon;
            return (
              <div
                key={f.file_id}
                className="flex items-center gap-3 bg-white/[0.03] border border-white/5 rounded-lg px-3 py-2"
                data-testid={`attached-file-${f.file_id}`}
              >
                <Icon className="w-4 h-4 text-amber-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-white/70 text-sm truncate">{f.original_name}</p>
                  <p className="text-white/30 text-xs">{formatSize(f.size)}</p>
                </div>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}${f.url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-amber-400 text-xs hover:underline flex-shrink-0"
                  data-testid={`view-file-${f.file_id}`}
                >
                  View
                </a>
                <button
                  onClick={(e) => { e.stopPropagation(); removeFile(f.file_id); }}
                  className="p-1 rounded hover:bg-red-500/10 text-white/30 hover:text-red-400 transition-colors flex-shrink-0"
                  data-testid={`remove-file-${f.file_id}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
